// src/app/shared/components/location-picker/location-picker.component.ts

/**
 * Composant Location Picker
 * Permet de sélectionner une adresse avec autocomplete et carte interactive.
 * Supporte un mode "base + événement" : affiche un marqueur fixe 🏠 pour la
 * base du marchand et un marqueur 🔵 draggable pour le lieu de l'événement.
 */

import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  inject,
  ChangeDetectorRef,
  PLATFORM_ID,
  input,
  output,
  effect,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import type * as L from 'leaflet';
import { LocationService } from '../../../services/location.service';
import {
  AddressSuggestion,
  ValidatedAddress,
  Coordinates,
  RouteResult,
} from '../../../models/location.models';
import { LogService } from '../../../services/log.service';

@Component({
  selector: 'app-location-picker',
  imports: [CommonModule, FormsModule],
  templateUrl: './location-picker.component.html',
  styleUrls: ['./location-picker.component.css'],
})
export class LocationPickerComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private log = inject(LogService);
  // IMPORTANT: static: false pour attendre que la vue soit rendue
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

  // Carte Leaflet
  private map!: L.Map;
  /** Marqueur bleu draggable pour le lieu de l'événement */
  private marker!: L.Marker;
  /** Marqueur fixe 🏠 de la base du marchand (non draggable) */
  private baseMarker?: L.Marker;
  private radiusCircle?: L.Circle;
  /** Centre fixe du rayon d'action (adresse de base du marchand) */
  private centerCoords: [number, number] | null = null;
  /** Tracé de la route OSRM affiché sur la carte */
  private routeLayer?: L.GeoJSON;
  /** Subject pour debounce les appels OSRM lors du drag */
  private routeSubject = new Subject<Coordinates>();
  mapInitialized = false;
  /** Force l'affichage du container carte (pour que le ViewChild soit dispo) */
  showMapContainer = false;

  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);
  private _L: typeof import('leaflet') | null = null;

  // Infos distance/trajet OSRM
  distanceFromCenterKm: number | null = null;
  travelTimeMin: number | null = null;
  /** true pendant le chargement de l'itinéraire */
  isLoadingRoute = false;
  /** true si le marqueur événement est hors du rayon d'action */
  isOutsideRadius = false;

  readonly initialAddress = input<string | undefined>(undefined);
  readonly preselectedAddress = input<ValidatedAddress | undefined>(undefined);
  /** Adresse de base du marchand — carte centrée dessus avec marqueur 🏠 fixe */
  readonly baseAddress = input<ValidatedAddress | undefined>(undefined);
  readonly showMap = input<boolean>(true);
  readonly mapHeight = input<string>('400px');
  /** Rayon d'action en km à afficher sur la carte (0 = désactivé) */
  readonly actionRadius = input<number>(0);

  readonly addressSelected = output<ValidatedAddress>();
  readonly addressValidated = output<ValidatedAddress>();
  /** Émis à chaque recalcul de route OSRM (null si OSRM indisponible ou adresse non sélectionnée) */
  readonly routeUpdated = output<RouteResult | null>();

  // États
  searchQuery: string = '';
  suggestions: AddressSuggestion[] = [];
  validatedAddress: ValidatedAddress | null = null;
  isSearching: boolean = false;
  isValidating: boolean = false;
  showSuggestions: boolean = false;
  validationError: string | null = null;

  // Coordonnées et adresse en temps réel
  currentCoordinates: Coordinates | null = null;
  currentAddress: string = '';
  isLoadingAddress: boolean = false;

  // Observables
  private searchSubject = new Subject<string>();
  private subscriptions = new Subscription();

  constructor(private locationService: LocationService) {
    // Effect: baseAddress changes (replaces ngOnChanges for baseAddress)
    effect(() => {
      const base = this.baseAddress();
      if (base) {
        this.centerCoords = [base.coordinates.lat, base.coordinates.lng];
        this.showMapContainer = true;
        if (!this.mapInitialized) {
          this.scheduleMapInit();
        } else {
          this.addBaseMarkerAndCircle();
        }
      }
    });

    // Effect: preselectedAddress changes (replaces ngOnChanges for preselectedAddress)
    effect(() => {
      const preselected = this.preselectedAddress();
      const base = this.baseAddress();
      if (preselected && base) {
        // Mode base + pré-sélection (duplication) : afficher l'adresse dans le champ
        // et positionner le marqueur événement sur l'adresse dupliquée
        this.validatedAddress = preselected;
        this.searchQuery = preselected.formatted || preselected.address;
        this.currentCoordinates = preselected.coordinates;
        this.currentAddress = this.searchQuery;
        if (this.mapInitialized) {
          this.addEventMarker([
            preselected.coordinates.lat,
            preselected.coordinates.lng,
          ]);
          this.updateTravelInfo(
            preselected.coordinates.lat,
            preselected.coordinates.lng,
          );
          this.fitBoundsForBoth();
        }
      } else if (preselected && !base) {
        this.validatedAddress = preselected;
        this.searchQuery = preselected.formatted || preselected.address;
        this.currentCoordinates = preselected.coordinates;
        this.currentAddress = this.searchQuery;
        this.centerCoords = [
          preselected.coordinates.lat,
          preselected.coordinates.lng,
        ];
        this.showMapContainer = true;
        if (!this.mapInitialized) {
          this.scheduleMapInit();
        } else {
          this.updateMarkerPosition(preselected.coordinates);
          if (this.actionRadius() > 0) {
            this.drawRadiusCircle(this.centerCoords!);
          }
        }
      }
    });

    // Effect: actionRadius changes (replaces ngOnChanges for actionRadius)
    effect(() => {
      const radius = this.actionRadius();
      if (radius > 0 && this.mapInitialized && this.centerCoords) {
        this.drawRadiusCircle(this.centerCoords);
      }
    });
  }

  ngOnInit(): void {
    // Configuration de la recherche avec debounce
    const searchSubscription = this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => {
          if (query.length < 3) {
            this.suggestions = [];
            this.showSuggestions = false;
            return [];
          }
          this.isSearching = true;
          return this.locationService.searchAddress(query);
        }),
      )
      .subscribe({
        next: (suggestions) => {
          this.suggestions = suggestions;
          this.showSuggestions = suggestions.length > 0;
          this.isSearching = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.log.error('Erreur recherche:', error);
          this.isSearching = false;
          this.showSuggestions = false;
          this.cdr.markForCheck();
        },
      });

    this.subscriptions.add(searchSubscription);

    // Debounce les appels OSRM (drag live)
    const routeSub = this.routeSubject
      .pipe(debounceTime(500))
      .subscribe((coords) => this.fetchRoute(coords.lat, coords.lng));
    this.subscriptions.add(routeSub);

    // Charger l'adresse pré-sélectionnée si fournie (mode onboarding classique)
    if (this.preselectedAddress()) {
      this.validatedAddress = this.preselectedAddress()!;
      this.searchQuery =
        this.preselectedAddress()!.formatted ||
        this.preselectedAddress()!.address;
      this.currentCoordinates = this.preselectedAddress()!.coordinates;
      this.currentAddress =
        this.preselectedAddress()!.formatted ||
        this.preselectedAddress()!.address;
    } else if (this.initialAddress()) {
      this.searchQuery = this.initialAddress()!;
      this.validateCurrentAddress();
    }
    // NOTE : en mode baseAddress, on ne pré-remplit PAS searchQuery
  }

  ngAfterViewInit(): void {
    // Mode base + événement : initialiser la carte centrée sur la base
    if (this.baseAddress()) {
      this.centerCoords = [
        this.baseAddress()!.coordinates.lat,
        this.baseAddress()!.coordinates.lng,
      ];
      this.showMapContainer = true;
      this.scheduleMapInit();
      return;
    }

    // Mode onboarding classique : initialiser si adresse pré-sélectionnée
    if (this.preselectedAddress() && this.currentCoordinates) {
      this.showMapContainer = true;
      this.scheduleMapInit();
    }
  }

  /** Planifie l'initialisation de la carte (attend le DOM avec retries) */
  private scheduleMapInit(): void {
    if (this.mapInitialized || !isPlatformBrowser(this.platformId)) return;
    import('leaflet').then((leaflet) => {
      // Interop CJS/ESM : le chunk généré par esbuild pour cet import
      // dynamique expose parfois le module Leaflet (UMD, sans marqueur
      // `__esModule`) uniquement via `.default`, sans copier les exports
      // nommés à plat (contrairement à un `import * as L from 'leaflet'`
      // statique, où l'interop est réappliquée localement) — `L.map`
      // devenait alors `undefined` (bug constaté 2026-07-08).
      const withDefault = leaflet as unknown as { default?: typeof leaflet };
      this._L = withDefault.default ?? leaflet;
      let attempt = 0;
      const tryInit = () => {
        attempt++;
        if (this.mapInitialized) return;
        if (this.mapContainer?.nativeElement) {
          this.initializeMap();
        } else if (attempt < 15) {
          setTimeout(tryInit, 200);
        } else {
          this.log.warn(
            'LocationPicker: mapContainer not found after 15 attempts',
          );
        }
      };
      // Premier essai après un tick Angular pour laisser le *ngIf rendre le DOM
      setTimeout(tryInit, 50);
    });
  }

  /** Met à jour les infos trajet via OSRM (mode base uniquement) */
  private updateTravelInfo(lat: number, lng: number): void {
    if (!this.centerCoords) return;
    // Vérifier hors rayon immédiatement (haversine rapide)
    this.checkRadiusBounds(lat, lng);
    // Déclencher le calcul OSRM debounced
    this.routeSubject.next({ lat, lng });
  }

  /** Appelle le backend proxy OSRM et affiche le tracé + infos */
  private fetchRoute(lat: number, lng: number): void {
    if (!this.centerCoords || !this.baseAddress()) return;
    this.isLoadingRoute = true;
    const from: Coordinates = {
      lat: this.centerCoords[0],
      lng: this.centerCoords[1],
    };
    const to: Coordinates = { lat, lng };
    this.locationService.getRoute(from, to).subscribe({
      next: (routes) => {
        this.isLoadingRoute = false;
        if (routes.length > 0) {
          const best = routes[0];
          this.distanceFromCenterKm = best.distanceKm;
          this.travelTimeMin = best.durationMin;
          this.drawRoute(best.geometry);
          this.routeUpdated.emit(best);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoadingRoute = false;
        // Fallback haversine si OSRM indisponible
        if (this.centerCoords) {
          const dist = this.haversineKm(
            this.centerCoords[0],
            this.centerCoords[1],
            lat,
            lng,
          );
          this.distanceFromCenterKm = Math.round(dist * 10) / 10;
          this.travelTimeMin = Math.round((dist / 80) * 60);
          this.clearRoute();
        }
        this.routeUpdated.emit(null);
        this.cdr.markForCheck();
      },
    });
  }

  /** Dessine le tracé de la route sur la carte */
  private drawRoute(geometry: GeoJSON.LineString): void {
    if (!this.map) return;
    const L = this._L!;
    this.clearRoute();
    this.routeLayer = L.geoJSON(geometry as any, {
      style: {
        color: '#3b82f6',
        weight: 4,
        opacity: 0.7,
      },
    }).addTo(this.map);
  }

  /** Supprime le tracé de la route */
  private clearRoute(): void {
    if (this.routeLayer) {
      this.routeLayer.remove();
      this.routeLayer = undefined;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.clearRoute();
    if (this.map) {
      this.map.remove();
    }
  }

  /**
   * Initialise la carte Leaflet une seule fois.
   * En mode baseAddress → centrée sur la base, marqueur 🏠 fixe + cercle.
   * En mode onboarding → centrée sur preselectedAddress avec marqueur draggable.
   */
  private initializeMap(): void {
    if (this.mapInitialized || !this.mapContainer) return;
    const L = this._L!;
    try {
      // Déterminer le centre initial
      let initialCoords: [number, number];
      if (this.centerCoords) {
        initialCoords = this.centerCoords;
      } else if (this.currentCoordinates) {
        initialCoords = [
          this.currentCoordinates.lat,
          this.currentCoordinates.lng,
        ];
      } else {
        initialCoords = [48.8566, 2.3522]; // Paris par défaut
      }

      // Zoom adapté au rayon d'action (mode base) ou par défaut
      let initialZoom = 13;
      if (this.actionRadius() > 0) {
        initialZoom =
          this.actionRadius() <= 10
            ? 12
            : this.actionRadius() <= 30
              ? 10
              : this.actionRadius() <= 60
                ? 9
                : 8;
      }

      this.map = L.map(this.mapContainer.nativeElement, {
        center: initialCoords,
        zoom: initialZoom,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(this.map);

      this.mapInitialized = true;

      // ── Mode base + événement ──
      if (this.baseAddress()) {
        this.addBaseMarkerAndCircle();
        // Si une adresse événement est pré-sélectionnée (duplication), placer le marqueur dessus
        const pre = this.preselectedAddress();
        if (pre) {
          const eventCoords: [number, number] = [
            pre.coordinates.lat,
            pre.coordinates.lng,
          ];
          this.addEventMarker(eventCoords);
          this.currentCoordinates = pre.coordinates;
          this.searchQuery = pre.formatted || pre.address;
          this.currentAddress = this.searchQuery;
          this.validatedAddress = pre;
          this.updateTravelInfo(pre.coordinates.lat, pre.coordinates.lng);
          this.fitBoundsForBoth();
        } else {
          // Placer le marqueur événement (draggable) au même endroit que la base
          this.addEventMarker(initialCoords);
          if (!this.currentCoordinates) {
            this.currentCoordinates = {
              lat: initialCoords[0],
              lng: initialCoords[1],
            };
          }
        }
      } else {
        // ── Mode onboarding classique : marqueur draggable ──
        this.addEventMarker(initialCoords);
        if (!this.currentCoordinates) {
          this.currentCoordinates = {
            lat: initialCoords[0],
            lng: initialCoords[1],
          };
        }
        if (this.actionRadius() > 0 && this.centerCoords) {
          this.drawRadiusCircle(this.centerCoords);
        }
      }

      setTimeout(() => {
        if (this.map) this.map.invalidateSize();
      }, 200);
    } catch (error) {
      this.log.error("Erreur lors de l'initialisation de la carte:", error);
    }
  }

  /**
   * Ajoute le marqueur 🏠 fixe de la base + cercle d'action
   */
  private addBaseMarkerAndCircle(): void {
    if (!this.map || !this.centerCoords) return;
    const L = this._L!;
    // Supprimer l'ancien marqueur base si présent
    if (this.baseMarker) {
      this.baseMarker.remove();
    }

    this.baseMarker = L.marker(this.centerCoords, {
      draggable: false,
      icon: this.getBaseIcon(),
    }).addTo(this.map);

    // Dessiner le cercle d'action et adapter le zoom
    if (this.actionRadius() > 0) {
      this.drawRadiusCircle(this.centerCoords);
      // fitBounds sur le cercle pour que tout le rayon soit visible
      if (this.radiusCircle) {
        this.map.fitBounds(this.radiusCircle.getBounds(), {
          padding: [30, 30],
        });
      }
    } else {
      this.map.setView(this.centerCoords, 13);
    }
  }

  /**
   * Ajoute ou déplace le marqueur 🔵 draggable (événement)
   */
  private addEventMarker(coords: [number, number]): void {
    if (!this.map) return;
    const L = this._L!;
    if (this.marker) {
      this.marker.setLatLng(coords);
    } else {
      this.marker = L.marker(coords, {
        draggable: true,
        icon: this.getCustomIcon(),
      }).addTo(this.map);

      // Drag live : debounce le calcul de route
      this.marker.on('drag', () => {
        const pos = this.marker.getLatLng();
        // Check rayon immédiat (pas de latence réseau)
        this.checkRadiusBounds(pos.lat, pos.lng);
        // Calcul route debounced
        this.routeSubject.next({ lat: pos.lat, lng: pos.lng });
        this.cdr.markForCheck();
      });

      this.marker.on('dragend', () => {
        const pos = this.marker.getLatLng();
        const coords = { lat: pos.lat, lng: pos.lng };
        this.currentCoordinates = coords;
        this.onMarkerMoved(coords);
        this.cdr.markForCheck();
      });
    }
  }

  /**
   * Ajuste la vue pour montrer la base ET le lieu d'événement
   */
  private fitBoundsForBoth(): void {
    if (!this.map || !this.centerCoords || !this.currentCoordinates) return;
    const L = this._L!;
    const bounds = L.latLngBounds(
      [this.centerCoords[0], this.centerCoords[1]],
      [this.currentCoordinates.lat, this.currentCoordinates.lng],
    );
    this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
  }

  /**
   * Vérifie si les coordonnées sont dans le rayon d'action
   */
  private checkRadiusBounds(lat: number, lng: number): void {
    if (!this.centerCoords || this.actionRadius() <= 0) {
      this.isOutsideRadius = false;
      return;
    }
    const dist = this.haversineKm(
      this.centerCoords[0],
      this.centerCoords[1],
      lat,
      lng,
    );
    this.isOutsideRadius = dist > this.actionRadius();
  }

  /**
   * Met à jour la position du marker et recentre la carte
   */
  private updateMarker(coordinates: Coordinates): void {
    this.currentCoordinates = coordinates;

    if (!this.mapInitialized) {
      // IMPORTANT : ne JAMAIS appeler initializeMap() directement ici.
      // initializeMap() utilise this._L (le module Leaflet), qui n'est
      // peuplé que par scheduleMapInit() via l'import dynamique
      // import('leaflet'). Lors d'une recherche libre (pas de baseAddress
      // ni preselectedAddress), ngAfterViewInit() ne déclenche jamais
      // scheduleMapInit() : appeler initializeMap() ici plantait alors sur
      // `this._L!.map(...)` (this._L === null), erreur avalée par le
      // try/catch interne et loguée en silence — la carte ne s'affichait
      // jamais après la sélection d'une adresse (bug constaté 2026-07-07).
      this.showMapContainer = true;
      this.scheduleMapInit();
    } else {
      this.updateMarkerPosition(coordinates);
    }
  }

  /**
   * Met à jour la position du marker (helper)
   */
  private updateMarkerPosition(coordinates: Coordinates): void {
    if (!this.map) return;

    const latLng: [number, number] = [coordinates.lat, coordinates.lng];

    // En mode base, créer/déplacer le marqueur événement
    if (this.baseAddress()) {
      this.addEventMarker(latLng);
      this.updateTravelInfo(coordinates.lat, coordinates.lng);
      this.fitBoundsForBoth();
    } else {
      // Mode onboarding classique
      if (this.marker) {
        this.marker.setLatLng(latLng);
      }
      this.map.panTo(latLng);
      this.updateTravelInfo(coordinates.lat, coordinates.lng);
    }

    setTimeout(() => {
      if (this.map) this.map.invalidateSize();
    }, 100);
  }

  /**
   * Dessine (ou met à jour) le cercle de rayon d'action sur la carte
   */
  private drawRadiusCircle(center: [number, number]): void {
    if (!this.map) return;
    const L = this._L!;
    if (this.radiusCircle) {
      this.radiusCircle.setLatLng(center);
      this.radiusCircle.setRadius(this.actionRadius() * 1000);
    } else {
      this.radiusCircle = L.circle(center, {
        radius: this.actionRadius() * 1000,
        color: '#6366f1',
        fillColor: '#818cf8',
        fillOpacity: 0.1,
        weight: 3,
        dashArray: '8 6',
      }).addTo(this.map);
    }
  }

  /** Distance haversine en km */
  private haversineKm(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
  ): number {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /**
   * Gestion de la saisie dans l'input de recherche
   */
  onSearchInput(): void {
    // Ne pas réinitialiser validatedAddress si on tape juste du texte
    // Seulement réinitialiser l'erreur
    this.validationError = null;
    this.searchSubject.next(this.searchQuery);
  }

  /**
   * Gestion du focus sur l'input
   */
  onSearchFocus(): void {
    // Réafficher les suggestions si on avait déjà des résultats
    if (this.suggestions.length > 0 && this.searchQuery.length >= 3) {
      this.showSuggestions = true;
    }
  }

  /**
   * Sélection d'une suggestion
   */
  selectSuggestion(suggestion: AddressSuggestion): void {
    this.log.debug(
      '🟢 [Picker] selectSuggestion:',
      suggestion.label,
      '| city:',
      suggestion.city,
      '| postalCode:',
      suggestion.postalCode,
    );
    this.searchQuery = suggestion.label;
    this.showSuggestions = false;
    this.suggestions = [];

    // Mettre à jour les coordonnées
    this.currentCoordinates = suggestion.coordinates;
    this.currentAddress = suggestion.label;

    // Valider automatiquement — passer city/postalCode de la suggestion
    this.validateAddress(
      suggestion.label,
      suggestion.coordinates,
      suggestion.city,
      suggestion.postalCode,
    );
  }

  /**
   * Validation de l'adresse actuelle
   */
  validateCurrentAddress(): void {
    if (!this.searchQuery || this.searchQuery.length < 5) {
      this.validationError = 'Veuillez saisir une adresse valide';
      return;
    }

    this.validateAddress(this.searchQuery);
  }

  /**
   * Valide une adresse ou utilise directement des coordonnées
   */
  private validateAddress(
    address?: string,
    coordinates?: Coordinates,
    city?: string,
    postalCode?: string,
  ): void {
    this.isValidating = true;
    this.validationError = null;

    if (coordinates) {
      // Si on a déjà des coordonnées, on n'appelle pas le backend
      this.validatedAddress = {
        valid: true,
        address: address || 'Adresse sélectionnée',
        city: city || '',
        postalCode: postalCode || '',
        country: 'France',
        coordinates,
        formatted:
          address ||
          `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`,
        confidence: 100,
        source: 'autocomplete',
      };
      this.log.debug(
        '🟡 [Picker] emit addressSelected:',
        this.validatedAddress.formatted,
        '| city:',
        this.validatedAddress.city,
      );
      this.addressValidated.emit(this.validatedAddress);
      this.addressSelected.emit(this.validatedAddress); // Émettre automatiquement
      this.updateMarker(coordinates);
      this.isValidating = false;
    } else if (address) {
      // Sinon, appeler le backend pour validation
      this.locationService.validateAddress(address).subscribe({
        next: (validated) => {
          this.isValidating = false;

          if (!validated) {
            this.validationError = 'Adresse non trouvée ou ambiguë';
            this.cdr.markForCheck();
            return;
          }

          this.validatedAddress = validated;
          this.currentCoordinates = validated.coordinates;
          this.currentAddress = validated.formatted;
          this.addressValidated.emit(validated);
          this.addressSelected.emit(validated); // Émettre automatiquement
          this.updateMarker(validated.coordinates);
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isValidating = false;
          this.validationError =
            error.message || 'Erreur lors de la validation';
          this.cdr.markForCheck();
        },
      });
    } else {
      this.isValidating = false;
      this.validationError = 'Adresse ou coordonnées manquantes';
    }
  }

  /**
   * Utilise la géolocalisation du navigateur
   */
  useCurrentLocation(): void {
    this.isValidating = true;
    this.validationError = null;

    this.locationService.getAddressFromCurrentPosition().subscribe({
      next: (validated) => {
        this.isValidating = false;

        if (validated) {
          this.searchQuery = validated.formatted;
          this.validatedAddress = validated;
          this.currentCoordinates = validated.coordinates;
          this.currentAddress = validated.formatted;
          this.addressValidated.emit(validated);
          this.addressSelected.emit(validated); // Émettre automatiquement
          this.updateMarker(validated.coordinates);
        } else {
          this.validationError = 'Adresse non trouvée';
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isValidating = false;
        // Message adapté selon le type d'erreur
        if (error.message && error.message.includes('refusée')) {
          this.validationError =
            '🔒 Pas de souci ! Recherchez manuellement votre adresse ci-dessus.';
        } else {
          this.validationError =
            error.message || 'Erreur lors de la géolocalisation';
        }
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Confirme la sélection
   */
  confirmSelection(): void {
    if (this.validatedAddress) {
      this.addressSelected.emit(this.validatedAddress);
    }
  }

  /**
   * Gestion du déplacement du marqueur
   */
  private onMarkerMoved(coordinates: Coordinates): void {
    this.isValidating = true;
    this.updateAddressFromCoordinates(coordinates);
  }

  /**
   * Met à jour l'adresse depuis les coordonnées (reverse geocoding)
   */
  private updateAddressFromCoordinates(coordinates: Coordinates): void {
    this.isLoadingAddress = true;

    this.locationService
      .reverseGeocode(coordinates.lat, coordinates.lng)
      .subscribe({
        next: (validated) => {
          this.isValidating = false;
          this.isLoadingAddress = false;
          if (validated) {
            this.validatedAddress = validated;
            this.searchQuery = validated.formatted;
            this.currentAddress = validated.formatted;
            this.currentCoordinates = coordinates;
            this.log.debug(
              '🟡 [Picker] marker dragged → emit addressSelected:',
              validated.formatted,
            );
            this.addressValidated.emit(validated);
            this.addressSelected.emit(validated);
          } else {
            this.currentAddress = 'Adresse non trouvée';
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.isValidating = false;
          this.isLoadingAddress = false;
          this.currentAddress = "Erreur lors de la récupération de l'adresse";
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Icône personnalisée pour le marqueur événement (bleu)
   */
  private getCustomIcon(): L.Icon {
    const L = this._L!;
    return L.icon({
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      iconRetinaUrl:
        'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      shadowUrl:
        'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });
  }

  /**
   * Icône maison 🏠 pour le marqueur de base (non-draggable)
   */
  private getBaseIcon(): L.DivIcon {
    const L = this._L!;
    return L.divIcon({
      html: '<div style="font-size:32px;line-height:1;filter:drop-shadow(0 2px 4px rgba(0,0,0,.4));text-align:center">🏠</div>',
      className: '', // Vide pour supprimer le style par défaut de Leaflet (background blanc + bordure)
      iconSize: [36, 36],
      iconAnchor: [18, 36],
      popupAnchor: [0, -36],
    });
  }

  /**
   * Ferme les suggestions
   */
  closeSuggestions(): void {
    // Délai pour permettre le clic sur une suggestion
    setTimeout(() => {
      this.showSuggestions = false;
    }, 200);
  }
}
